import { useDisclosure } from "@msuite/ui/hooks";
import { Icon as IconComponent, type IconType } from "@msuite/ui/icons";
import type { MenuItems } from "@msuite/ui/menu";
import type {
	ChildrenProps,
	ClassNameProps,
	ContentClassNameProps,
	ContentStyleProps,
	StyleProps,
} from "@msuite/ui/types";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { motion, useDragControls } from "motion/react";
import React from "react";
import { useBreadcrumbTitle } from "../app-shell/use-breadcrumb-title";
import { ContentCardMenu } from "./content-card-menu";
import { ContentCardScrollContainer } from "./content-card-scroll-container";

/** Props Interface */
interface ContentCardProps
	extends ChildrenProps,
		ClassNameProps,
		StyleProps,
		ContentClassNameProps,
		ContentStyleProps,
		VariantProps<typeof contentCardContent> {
	title: string;
	icon: IconType | (() => React.ReactNode) | React.ReactNode;
	defaultIsOpen?: boolean;
	isCollapsible?: boolean;
	menuItems?: MenuItems;
	menuLabel?: string;
	id?: string;
	layout?: boolean;
	layoutId?: string;
	as?: React.ElementType;
	onSubmit?: (event: React.FormEvent) => void;
	draggable?: boolean;
	/** A URL segment the breadcrumbs name after this card while it is mounted — see `useBreadcrumbTitle`. */
	identifier?: string;
	/** The name the breadcrumbs show for `identifier`, when it is not the card's title. */
	dataPageBreadcrumbsTitle?: string;
	/** Rendered at the right end of the header, before the menu — a state `Tag`, a count. */
	accessory?: React.ReactNode;
}

function ContentCard({
	title,
	icon,
	children,
	className,
	style,
	defaultIsOpen = true,
	menuItems = [],
	menuLabel,
	contentClassName,
	contentStyle,
	isCollapsible = true,
	variant,
	id,
	as: _as = "div",
	onSubmit,
	draggable,
	identifier,
	dataPageBreadcrumbsTitle,
	accessory,
}: ContentCardProps) {
	/** Hooks */
	const disclosure = useDisclosure({ defaultIsOpen });
	const contentCardContentClasses = contentCardContent({ variant });
	const dragControls = useDragControls();
	useBreadcrumbTitle(identifier, dataPageBreadcrumbsTitle || title);

	/** Props */
	const Component = motion[_as as keyof typeof motion] as React.ElementType;
	const iconNode = typeof icon === "function" ? icon({ className: "stroke-primary-500" }) : icon;

	/** Render */
	return (
		<Component
			id={id}
			className={clsx(
				"gap-0 p-0.5 border-[0.5px] rounded-base card v-stack",
				disclosure.isOpen ? "bg-primary-50 dark:bg-primary-900/70" : "card-bg",
				className,
			)}
			style={style}
			onSubmit={onSubmit}
			drag={draggable}
			dragElastic={false}
			dragMomentum={false}
			dragListener={false}
			dragControls={dragControls}
		>
			<div
				className="flex flex-row justify-between items-center px-3 py-1.5 font-medium text-sm cursor-default"
				onDoubleClick={isCollapsible ? disclosure.onToggle : undefined}
			>
				<div className="flex flex-row items-center gap-2 select-none">
					{draggable ? (
						<div
							onPointerDown={(e) => dragControls.start(e)}
							className="cursor-grab active:cursor-grabbing"
						>
							<IconComponent name="GripVertical" />
						</div>
					) : null}
					{iconNode}
					<p>{title}</p>
				</div>
				<div className="flex flex-row items-center gap-2">
					{accessory}
					<ContentCardMenu
						disclosure={disclosure}
						menuItems={menuItems}
						menuLabel={menuLabel}
						isCollapsible={isCollapsible}
					/>
				</div>
			</div>
			<div
				className={clsx(disclosure.isOpen ? contentCardContentClasses : "hidden", contentClassName)}
				style={contentStyle}
			>
				{children}
			</div>
		</Component>
	);
}

ContentCard.ScrollContainer = ContentCardScrollContainer;

const contentCardContent = cva(
	["flex flex-col gap-2 p-4 border-[0.5px] rounded-p-0.5 card", "overflow-hidden"],
	{
		variants: {
			variant: {
				default: [],
				plain: ["bg-transparent border-0! shadow-none p-2! pt-1!"],
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

export { ContentCard };
